import { BaseDragger } from "./BaseDragger";
import type {
  Point2D,
  Point3D,
  DraftSegmentInfo,
  SegmentDelta,
  TypedInputSpec,
} from "./types";
import {
  RELATIVE_POINT_MODES,
  deltaFromRelativeInput,
} from "./relativePointInput";
import type { DrawingModule } from "../types/DrawingWeb";
import { isShortcutBlockingTarget } from "@/utils/keyboardGuards";

/**
 * Abstract base class for sequential draggers (tablet-first) with incremental entity creation.
 *
 * Interaction model:
 * 1. Start drag → begin positioning a new point
 * 2. Drag → adjust point position (shows rubber band preview)
 * 3. Lift stylus (end) → fix the point at that location
 * 4. Start new drag → begin positioning next point
 * 5. Escape → complete the shape (open)
 * 6. Release near start point (green circle) → complete and close the shape
 *
 * Zoom/pan support:
 * - Drawing state is preserved when switching to pan/zoom draggers
 * - User can zoom/pan and then continue adding points
 *
 * Entity creation:
 * - First point: Creates entity with 1 vertex, executes AddEntityCommand
 * - Subsequent points: Executes AddPointToPolylineCommand
 * - Undo: Calls viewer.undoCommand() and updates internal state
 * - Auto-cancel: If all points are undone, clears state
 *
 * Subclasses implement:
 * - createEntityWithFirstPoint(): Create entity on first point
 * - addPointToEntity(): Add subsequent points via command
 * - onComplete(): Handle close/finish
 * - updatePreview(): Show rubber band preview
 * - updatePreviewWithPoints(): Show point markers
 */
export abstract class SequentialDragger extends BaseDragger {
  protected previewIds: string[] = [];
  protected screenPoints: Point2D[] = [];
  protected worldPoints: Point3D[] = [];

  // Entity tracking
  protected currentEntityId: DrawingModule.OdDbObjectId | null = null;
  protected commandCount = 0;

  // For detecting close to start point
  private readonly CLOSE_THRESHOLD = 25; // pixels

  // Keyboard event handler reference for cleanup
  private keyDownHandler: ((ev: KeyboardEvent) => void) | null = null;

  // Abonnés à hasPendingPlacement() (bouton Échap/Terminer de la barre du bas,
  // cf. SelectionMenu.vue) — même contrat que TwoPointDragger.
  private pendingChangeHandlers: ((pending: boolean) => void)[] = [];

  // View-changed (zoom/pan/pinch/extents) subscription for cleanup — used to
  // reproject the preview after navigation instead of a raw "wheel" listener,
  // which misses pan and touch pinch-zoom.
  private unsubscribeViewChanged: (() => void) | null = null;

  // Live draft segment (last fixed vertex + delta to current drag/typed
  // position), for UI like a typed-measurement input panel.
  private draftInfo: DraftSegmentInfo | null = null;

  // The most recent typed (not dragged) preview delta, if any — remembered
  // so the preview can be redrawn at the correct screen position after a
  // zoom/pan, since (unlike a real drag) no further pointer event will come
  // along to naturally refresh it.
  private lastTypedDelta: SegmentDelta | null = null;

  // Points popped by undoLastPoint(), in undo order (most recently undone
  // last), so redoLastPoint() can re-push the correct one back onto
  // worldPoints/screenPoints/points in sync with CommandHistory's own redo
  // stack. Cleared whenever a genuinely new point is added — same
  // "branching history" rule CommandHistory itself applies to its redo
  // stack on execute().
  private redoPoints: Point3D[] = [];

  /**
   * Return the last committed vertex as snap reference for ortho/polar.
   * This ensures polar angle is calculated relative to the previous vertex,
   * not the position where the user starts a new drag.
   */
  override getSnapReferencePoint(): Point3D | null {
    if (this.worldPoints.length > 0) {
      return this.worldPoints[this.worldPoints.length - 1];
    }
    return null;
  }

  // Points are fixed on release: where the drag starts is not one of them.
  override snapsToGestureStart(): boolean {
    return false;
  }

  // ============== Draft Segment / Typed Input ==============

  /**
   * Set the current draft segment and notify subscribers (e.g. a typed-
   * measurement input panel). Called whenever a point is fixed/undone
   * (delta reset to 0) and whenever updatePreview computes a live delta
   * during a drag or typed preview.
   */
  protected setDraftSegment(info: DraftSegmentInfo | null): void {
    this.draftInfo = info;
    this.emitTypedInputChange();
  }

  /**
   * The next vertex, typed relative to the last fixed one. Offered for as long
   * as the tool is active, so the pad can be toggled open before the first
   * vertex exists — it then shows empty fields (no values) and preview/commit
   * are no-ops until there is something to measure from.
   */
  override getTypedInputSpec(): TypedInputSpec | null {
    if (!this.isActive) return null;
    const draft = this.draftInfo;
    return {
      titleKey: "canvas:tools.options.keypad.title.measure",
      // The vertex being measured from: moving it starts a new segment.
      stepId: draft ? `${draft.origin.x},${draft.origin.y}` : "",
      modes: RELATIVE_POINT_MODES,
      values: draft
        ? {
            dx: draft.dx,
            dy: draft.dy,
            length: draft.length,
            angleDeg: draft.angleDeg,
          }
        : {},
    };
  }

  override previewTypedInput(
    modeId: string,
    values: Record<string, number>
  ): void {
    const delta = deltaFromRelativeInput(modeId, values);
    if (delta) this.previewTypedSegment(delta);
  }

  override commitTypedInput(
    modeId: string,
    values: Record<string, number>
  ): void {
    const delta = deltaFromRelativeInput(modeId, values);
    if (delta) this.commitTypedSegment(delta);
  }

  private previewTypedSegment(delta: SegmentDelta): void {
    if (this.worldPoints.length === 0) return;
    this.lastTypedDelta = delta;
    const origin = this.worldPoints[this.worldPoints.length - 1];
    const world: Point3D = {
      x: origin.x + delta.dx,
      y: origin.y + delta.dy,
      z: origin.z,
    };
    const screen = this.viewer.worldToScreen(world);
    this.updatePreview(screen, delta);
    this.viewer.update();
  }

  private commitTypedSegment(delta: SegmentDelta): void {
    if (this.worldPoints.length === 0) return;
    // Ignore a no-op commit (e.g. Enter pressed before typing anything) —
    // don't create a degenerate zero-length vertex.
    if (delta.dx === 0 && delta.dy === 0) return;
    const origin = this.worldPoints[this.worldPoints.length - 1];
    const world: Point3D = {
      x: origin.x + delta.dx,
      y: origin.y + delta.dy,
      z: origin.z,
    };
    const screen = this.viewer.worldToScreen(world);
    // Deliberately bypasses the close-on-proximity check in onEnd: that's a
    // pixel-distance heuristic for a drag-release gesture landing near the
    // start point by chance, not something a typed exact value should be
    // reinterpreted through.
    this.addPoint(screen, world);
  }

  // ============== Lifecycle ==============

  protected onActivate(): void {
    // Listen for keyboard shortcuts
    this.keyDownHandler = (ev: KeyboardEvent) => {
      if (isShortcutBlockingTarget(ev.target, ev.key)) {
        return;
      }

      if (ev.key === "Escape") {
        this.finishDrawing();
      }
      // Ctrl+Z/Cmd+Z is handled centrally by HistoryManager.undo(), which
      // calls handleUndo() below on the active dragger before falling back
      // to normal command-history undo — see handleUndo() for why this
      // can't also be handled here (it would double-undo).
    };
    window.addEventListener("keydown", this.keyDownHandler);

    // Reproject the preview after any navigation change (zoom, pan, pinch,
    // zoom-extents — a superset of the "wheel" event this used to listen
    // for, which missed pan and touch pinch-zoom).
    this.unsubscribeViewChanged = this.viewer.onViewChanged(() => {
      if (this.worldPoints.length === 0) return;
      // Wait a frame so the new view transform is applied before reprojecting.
      requestAnimationFrame(() => this.reprojectPreview());
    });

    // If we have existing points (resumed after zoom/pan), update overlay positions
    if (this.worldPoints.length > 0) {
      this.updatePreviewWithPoints();
      this.viewer.update();
    }

    // The spec depends on isActive, so tell the keypad it just became available.
    this.emitTypedInputChange();
  }

  protected onDeactivate(): void {
    // finish current entity
    this.finishDrawing();
    // Remove keyboard listener
    if (this.keyDownHandler) {
      window.removeEventListener("keydown", this.keyDownHandler);
      this.keyDownHandler = null;
    }
    // Remove view-changed subscription
    if (this.unsubscribeViewChanged) {
      this.unsubscribeViewChanged();
      this.unsubscribeViewChanged = null;
    }
    // Clear preview overlays (they have stale screen positions after zoom/pan)
    // but preserve worldPoints so we can recreate overlays when re-activated
    this.clearPreview();
    this.emitTypedInputChange();
  }

  /**
   * Redraw the preview at the current view transform after a zoom/pan.
   * If a typed value is active (no pointer drag in progress), reproject it
   * from the remembered delta — nothing else will trigger a redraw for it.
   * Otherwise just reposition the fixed-point markers/close indicator; a
   * live drag's next pointer-move event will refresh the rubber band itself.
   */
  private reprojectPreview(): void {
    if (this.worldPoints.length === 0) return;
    if (this.lastTypedDelta) {
      const origin = this.worldPoints[this.worldPoints.length - 1];
      const world: Point3D = {
        x: origin.x + this.lastTypedDelta.dx,
        y: origin.y + this.lastTypedDelta.dy,
        z: origin.z,
      };
      const screen = this.viewer.worldToScreen(world);
      this.updatePreview(screen, this.lastTypedDelta);
    } else {
      this.updatePreviewWithPoints();
    }
    this.viewer.update();
  }

  // ============== Public API ==============

  /**
   * Finish the current drawing.
   * Can be called externally (e.g., from a UI button) or via Escape key.
   */
  finishDrawing(): void {
    if (this.worldPoints.length >= 2 && this.currentEntityId) {
      this.clearPreview();
      this.onComplete(false, false);
      this.clearDrawingState();
      this.viewer.update();
    } else {
      // Not enough points, cancel
      this.cancelDrawing();
    }
  }

  /**
   * Undo the last point added.
   * Called via Ctrl+Z/Cmd+Z when drawing.
   */
  undoLastPoint(): void {
    if (this.commandCount === 0) {
      return;
    }

    // Call undo on the command history
    this.viewer.undoCommand();
    this.commandCount--;

    // Update our internal state — remember the removed point so a later
    // redo can restore it (see redoPoints above).
    if (this.screenPoints.length > 0) {
      this.redoPoints.push(this.worldPoints[this.worldPoints.length - 1]);
      this.screenPoints.pop();
      this.worldPoints.pop();
      this.points.pop();
    }

    // A lone point with no line isn't a state the user asked for — the
    // minimum this tool leaves behind is one full line (2 points). Undoing
    // down to exactly one point undoes that point too, collapsing straight
    // to "nothing" instead of stopping at a dangling anchor point.
    if (this.worldPoints.length === 1) {
      this.viewer.undoCommand();
      this.commandCount--;
      this.redoPoints.push(this.worldPoints[this.worldPoints.length - 1]);
      this.screenPoints.pop();
      this.worldPoints.pop();
      this.points.pop();
    }

    // Check for auto-cancel
    if (this.commandCount === 0) {
      // All commands undone (including AddEntityCommand)
      this.clearPreview();
      this.clearDrawingState();
      this.viewer.update();
      return;
    }

    // Reset the draft segment to the new last point.
    this.lastTypedDelta = null;
    if (this.worldPoints.length > 0) {
      const origin = this.worldPoints[this.worldPoints.length - 1];
      this.setDraftSegment({ origin, dx: 0, dy: 0, length: 0, angleDeg: 0 });
    }

    // Update preview with remaining points
    // updatePreviewWithPoints() handles its own cleanup
    this.updatePreviewWithPoints();
    this.viewer.update();
  }

  /**
   * Intercept undo while a shape is in progress, so command-history undo
   * and this dragger's own point-tracking (worldPoints/commandCount) stay
   * in sync — see BaseDragger.handleUndo() for why this exists.
   */
  override handleUndo(): boolean {
    if (this.commandCount === 0) {
      return false;
    }
    this.undoLastPoint();
    return true;
  }

  /**
   * Redo a point previously removed by undoLastPoint(). Symmetric to
   * undoLastPoint(): re-runs the command via viewer.redoCommand() and
   * re-pushes the same world point onto worldPoints/screenPoints/points so
   * the dragger's own state — and anything reading it, like the draft
   * segment shown by a typed-measurement panel — stays in sync with the
   * actual entity instead of silently lagging one point behind.
   */
  private redoLastPoint(): void {
    const world = this.redoPoints.pop();
    if (!world) return;

    this.viewer.redoCommand();
    this.commandCount++;

    const screen = this.viewer.worldToScreen(world);
    this.screenPoints.push(screen);
    this.worldPoints.push(world);
    this.points.push(world);

    // Mirror the collapse in undoLastPoint(): if that's the state this redo
    // is restoring from, redo the paired point too instead of stopping at a
    // lone point.
    if (this.worldPoints.length === 1 && this.redoPoints.length > 0) {
      const pairedWorld = this.redoPoints.pop()!;
      this.viewer.redoCommand();
      this.commandCount++;
      const pairedScreen = this.viewer.worldToScreen(pairedWorld);
      this.screenPoints.push(pairedScreen);
      this.worldPoints.push(pairedWorld);
      this.points.push(pairedWorld);
    }

    this.lastTypedDelta = null;
    // Use the current last point, not `world` — the paired-redo branch
    // above may have pushed a second, more recent point on top of it.
    const origin = this.worldPoints[this.worldPoints.length - 1];
    this.setDraftSegment({ origin, dx: 0, dy: 0, length: 0, angleDeg: 0 });

    this.updatePreviewWithPoints();
    this.viewer.update();
  }

  /**
   * Intercept redo while a shape is in progress (or has undone points that
   * could be redone), so command-history redo and this dragger's own
   * point-tracking stay in sync — see BaseDragger.handleRedo().
   */
  override handleRedo(): boolean {
    if (this.redoPoints.length === 0) {
      return false;
    }
    this.redoLastPoint();
    return true;
  }

  /**
   * Cancel the current drawing.
   * Undoes all commands and clears state.
   */
  cancelDrawing(): void {
    // Undo all commands we pushed
    while (this.commandCount > 0) {
      this.viewer.undoCommand();
      this.commandCount--;
    }
    this.clearPreview();
    this.clearDrawingState();
    this.viewer.update();
  }

  // ============== Dragger Callbacks ==============

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- world kept for BaseDragger's handleStart contract
  protected onStart(screen: Point2D, world: Point3D): void {
    // A new pointer gesture always supersedes any previous typed preview.
    this.lastTypedDelta = null;
    // Start showing preview for the new point being positioned
    this.updatePreview(screen);
    this.viewer.update();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- world kept for BaseDragger's handleMove contract
  protected onDrag(screen: Point2D, world: Point3D): void {
    // Update preview with current drag position (shows rubber band)
    this.updatePreview(screen);
    this.viewer.update();
  }

  protected onEnd(screen: Point2D, world: Point3D): void {
    // Close-on-proximity is a drag-release-only affordance ("did the user
    // let go near the start point?") — it doesn't apply to typed commits,
    // which go straight through addPoint() via commitTypedSegment().
    if (this.worldPoints.length >= 3) {
      const startWorld = this.worldPoints[0];
      const startScreen = this.viewer.worldToScreen(startWorld);
      const dx = screen.x - startScreen.x;
      const dy = screen.y - startScreen.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < this.CLOSE_THRESHOLD) {
        this.clearPreview();
        // Pas d'auto-sélection, comme pour la fin par Échap/bouton : l'outil
        // reste actif pour la forme suivante, une sélection résiduelle ne
        // ferait qu'empêcher Échap de « désélectionner » ce qui vient d'être
        // fermé.
        this.onComplete(true, false);
        this.clearDrawingState();
        this.viewer.update();
        return;
      }
    }

    this.addPoint(screen, world);
  }

  protected onCancel(): void {
    this.cancelDrawing();
  }

  /**
   * Pure mouse hover (no button held) while at least one vertex is fixed —
   * keeps the rubber band following the cursor between vertex clicks,
   * AutoCAD-style, instead of requiring a press-drag-release for every segment.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- world kept for BaseDragger's handleHoverMove contract
  override handleHoverMove(screen: Point2D, world: Point3D): void {
    if (this.worldPoints.length === 0) return;
    // Real pointer movement supersedes any typed preview, same as onStart().
    this.lastTypedDelta = null;
    this.updatePreview(screen);
    this.viewer.update();
  }

  /**
   * The pointer left the canvas while positioning a point — cancel just
   * that in-progress point instead of committing it at the last in-bounds
   * position. Nothing needs to be undone from worldPoints/commandCount:
   * addPoint() only runs from onEnd, so an in-progress drag hasn't
   * committed anything yet. Just revert the rubber-band back to the last
   * fixed vertex, mirroring the tail of undoLastPoint().
   */
  override handleLeave(): void {
    if (!this.isActive || !this.isDragging) return;
    this.isDragging = false;

    this.lastTypedDelta = null;
    if (this.worldPoints.length > 0) {
      const origin = this.worldPoints[this.worldPoints.length - 1];
      this.setDraftSegment({ origin, dx: 0, dy: 0, length: 0, angleDeg: 0 });
    }
    this.updatePreviewWithPoints();
    this.viewer.update();
  }

  // ============== Point Management ==============

  /**
   * Add (fix) a point to the shape.
   */
  protected addPoint(screen: Point2D, world: Point3D): void {
    const isFirstPoint = this.worldPoints.length === 0;
    if (isFirstPoint) this.emitPendingPlacementChange(true);

    // A genuinely new point branches away from any undone points that were
    // waiting to be redone — same rule CommandHistory applies to its own
    // redo stack on execute().
    this.redoPoints = [];

    // Store the point
    this.screenPoints.push(screen);
    this.worldPoints.push(world);
    this.points.push(world);

    if (isFirstPoint) {
      // Create entity with first point
      this.currentEntityId = this.createEntityWithFirstPoint(world);
      if (this.currentEntityId) {
        this.commandCount++;
      }
    } else {
      // Add point to existing entity via command
      if (this.currentEntityId) {
        this.addPointToEntity(
          this.currentEntityId,
          world,
          this.worldPoints.length - 1,
        );
        this.commandCount++;
      }
    }

    // Reset the draft segment to a zero-length delta from the new origin,
    // so a typed-measurement panel appears/resets the instant this point
    // is fixed.
    this.lastTypedDelta = null;
    this.setDraftSegment({
      origin: world,
      dx: 0,
      dy: 0,
      length: 0,
      angleDeg: 0,
    });

    // Update preview
    this.updatePreviewWithPoints();
    this.viewer.update();
  }

  /**
   * Clear drawing state (for when drawing is complete or cancelled).
   */
  protected clearDrawingState(): void {
    const hadPoints = this.worldPoints.length > 0;
    this.screenPoints = [];
    this.worldPoints = [];
    this.points = [];
    this.currentEntityId = null;
    this.commandCount = 0;
    this.lastTypedDelta = null;
    this.redoPoints = [];
    this.setDraftSegment(null);
    if (hadPoints) this.emitPendingPlacementChange(false);
  }

  // ============== Pending Placement (bouton Échap/Terminer tactile) ==============

  /** Un tracé est en cours dès qu'un premier sommet est posé. */
  override hasPendingPlacement(): boolean {
    return this.worldPoints.length > 0;
  }

  override onPendingPlacementChange(
    handler: (pending: boolean) => void,
  ): () => void {
    this.pendingChangeHandlers.push(handler);
    return () => {
      const index = this.pendingChangeHandlers.indexOf(handler);
      if (index >= 0) this.pendingChangeHandlers.splice(index, 1);
    };
  }

  /** Équivalent tactile d'Échap : termine (ou annule) le tracé en cours. */
  override cancelPendingPlacement(): void {
    this.finishDrawing();
  }

  private emitPendingPlacementChange(pending: boolean): void {
    for (const handler of this.pendingChangeHandlers) handler(pending);
  }

  // ============== Abstract Methods ==============

  /**
   * Create the entity with the first point.
   * Should execute AddEntityCommand.
   * @returns The entity ID, or null if creation failed
   */
  protected abstract createEntityWithFirstPoint(
    world: Point3D,
  ): DrawingModule.OdDbObjectId | null;

  /**
   * Add a point to the existing entity.
   * Should execute AddPointToPolylineCommand.
   */
  protected abstract addPointToEntity(
    entityId: DrawingModule.OdDbObjectId,
    world: Point3D,
    vertexIndex: number,
  ): void;

  /**
   * Called when the shape is completed.
   * @param closed Whether the shape should be closed
   * @param isAutoSelectEnabled allows to block autoSelect
   */
  protected abstract onComplete(
    closed: boolean,
    isAutoSelectEnabled?: boolean,
  ): void;

  /**
   * Update the preview with all fixed points (point markers, close indicator).
   */
  protected abstract updatePreviewWithPoints(): void;

  /**
   * Update preview with current drag position (rubber band from last point).
   * `exactDelta`, when provided (typed-measurement path), is the exact
   * world-space delta from the last fixed point and MUST be used instead of
   * re-deriving it from `screen` via screenToWorld, to avoid floating-point
   * rounding from the WASM transform corrupting a typed value. `screen` is
   * still used to position overlay labels/lines.
   */
  protected abstract updatePreview(
    screen: Point2D,
    exactDelta?: SegmentDelta,
  ): void;

  // ============== Utility Methods ==============

  /**
   * Clear all preview overlays.
   */
  protected clearPreview(): void {
    const overlay = this.viewer.overlayManager;
    if (overlay) {
      for (const id of this.previewIds) {
        overlay.remove(id);
      }
    }
    this.previewIds = [];
  }

  /**
   * Add a preview overlay ID to track for cleanup.
   */
  protected addPreviewId(id: string): void {
    this.previewIds.push(id);
  }

  protected clear(): void {
    super.clear();
    this.clearPreview();
    this.clearDrawingState();
  }
}
